import { useRef } from "react";
import "./FileUploader.css";

const ACCEPTED_FORMATS = {
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        ".docx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/webp": ".webp",
};

const ACCEPT_STRING = Object.keys(ACCEPTED_FORMATS).join(",");

const getFileIcon = (file) => {
    const type = file.type;
    if (type.startsWith("image/")) return "fa-file-image";
    if (type === "application/pdf") return "fa-file-pdf";
    if (type.includes("word")) return "fa-file-word";
    if (type.includes("sheet") || type.includes("excel")) return "fa-file-excel";
    return "fa-file";
};

const getFileIconColor = (file) => {
    const type = file.type;
    if (type.startsWith("image/")) return "#4CAF50";
    if (type === "application/pdf") return "#F44336";
    if (type.includes("word")) return "#2196F3";
    if (type.includes("sheet") || type.includes("excel")) return "#4CAF50";
    return "#757575";
};

const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

/**
 * @typedef {object} FileUploaderProps
 * @property {File[]} files - Currently selected files
 * @property {(files: File[]) => void} onChange - Callback when files change
 * @property {boolean} [disabled] - Whether the uploader is disabled
 * @property {number} [maxFiles] - Maximum number of files allowed (default: 5)
 * @property {number} [maxSizeMB] - Maximum size per file in MB (default: 10)
 */

/**
 * A reusable file uploader component supporting doc, pdf, xlsx, and images
 * @param {FileUploaderProps} props
 */
const FileUploader = ({
    files,
    onChange,
    disabled = false,
    maxFiles = 5,
    maxSizeMB = 10,
}) => {
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        const validFiles = [];
        const errors = [];

        selectedFiles.forEach((file) => {
            if (!Object.keys(ACCEPTED_FORMATS).includes(file.type)) {
                errors.push(`"${file.name}" is not a supported format`);
                return;
            }
            if (file.size > maxSizeMB * 1024 * 1024) {
                errors.push(`"${file.name}" exceeds ${maxSizeMB}MB limit`);
                return;
            }
            validFiles.push(file);
        });

        if (errors.length > 0) {
            alert(errors.join("\n"));
        }

        const totalFiles = files.length + validFiles.length;
        if (totalFiles > maxFiles) {
            alert(`You can upload a maximum of ${maxFiles} files`);
            const allowed = maxFiles - files.length;
            onChange([...files, ...validFiles.slice(0, allowed)]);
        } else {
            onChange([...files, ...validFiles]);
        }

        // Reset input so the same file can be re-selected
        e.target.value = "";
    };

    const handleRemove = (index) => {
        if (disabled) return;
        const updated = files.filter((_, i) => i !== index);
        onChange(updated);
    };

    return (
        <div className="file-uploader">
            <label className="file-uploader-label">
                Attachments <span className="file-uploader-optional">(Optional)</span>
            </label>

            {/* Drop zone / Upload button */}
            <div
                className={`file-uploader-dropzone ${disabled ? "disabled" : ""}`}
                onClick={() => !disabled && fileInputRef.current?.click()}
            >
                <i className="fa-solid fa-cloud-arrow-up file-uploader-icon"></i>
                <p className="file-uploader-text">
                    Click to upload files
                </p>
                <p className="file-uploader-hint">
                    PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF, WEBP (Max {maxSizeMB}MB
                    each, up to {maxFiles} files)
                </p>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPT_STRING}
                    onChange={handleFileSelect}
                    disabled={disabled}
                    style={{ display: "none" }}
                />
            </div>

            {/* Selected files list */}
            {files.length > 0 && (
                <div className="file-uploader-list">
                    {files.map((file, index) => (
                        <div key={index} className="file-uploader-item">
                            <div className="file-uploader-item-info">
                                <i
                                    className={`fa-solid ${getFileIcon(file)} file-uploader-item-icon`}
                                    style={{ color: getFileIconColor(file) }}
                                ></i>
                                <div className="file-uploader-item-details">
                                    <span className="file-uploader-item-name">{file.name}</span>
                                    <span className="file-uploader-item-size">
                                        {formatFileSize(file.size)}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="file-uploader-item-remove"
                                onClick={() => handleRemove(index)}
                                disabled={disabled}
                                title="Remove file"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploader;
